import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery } from "../axiosBaseQuery";
import type { Response } from "../interfaces";

// Enums matching backend
export enum QuestionType {
  MULTISELECT = 'multiselect',
  SINGLE_SELECT = 'single_select',
  DATE = 'date',
  TEXTBOX = 'textbox',
  TEXTAREA = 'textarea',
  ADDRESS = 'address',
  REVIEW_LINK = 'review_link',
  MULTISELECT_INPUT = 'multiselect_input',
  BIRTHDAY = 'birthday',
  CALENDER = 'calender',
  MOBILE = 'mobile',
  MULTISELECT_CHECKBOX = 'multiselect_checkbox',
  GIFT_BOXES = 'gift_boxes',
}

export enum QuestionOptionType {
  MULTISELECT = 'multiselect',
  BUTTON = 'button',
  TEXTBOX = 'textbox',
}

// Must match backend QuestionDatabaseField enum
export enum QuestionDatabaseField {
  NAME = 'name',
  EMAIL = 'email',
  ADDRESS = 'address',
  BIRTHDAY = 'birthday',
  SCHEDULE_DATE = 'schedule_date',
  RECEIVER_NOTE = 'receiver_note',
}

// Interfaces
export interface ChatFlow {
  flow_id: number;
  flow_name: string;
  start_question_id: number | null;
  is_live: boolean;
  is_delete: boolean;
  created_at?: string;
  updated_at?: string;
  chat_flow_questions?: ChatFlowQuestion[];
}

export interface QuestionOptionJson {
  option_text: string;
  option_placeholder_text?: string;
  option_type?: QuestionOptionType;
  next?: number | null;
  prev?: number | null;
  database_field?: string | null;
  is_active?: boolean;
  list_id?: number | null;
  image_url?: string;
}

export interface ChatFlowQuestion {
  question_id: number;
  flow_id: number;
  question_text: string;
  question_type: QuestionType;
  is_back_button: boolean;
  is_skip_button: boolean;
  next: number | null;
  prev: number | null;
  placeholder_text: string | null;
  database_field: QuestionDatabaseField | null;
  tags: string;
  is_generate_order: boolean;
  is_active: boolean;
  is_delete: boolean;
  created_at?: string;
  updated_at?: string;
  options?: QuestionOptionJson[]; // JSON format options (new)
  chat_flow_question_options?: ChatFlowQuestionOption[]; // Legacy format options
}

export interface ChatFlowQuestionOptionListItemRef {
  item_id: number;
  list_id: number;
  name: string;
  is_delete?: boolean;
}

export interface ChatFlowQuestionOption {
  option_id: number;
  question_id: number;
  list_id?: number | null;
  option_text: string;
  option_placeholder_text: string;
  option_type: QuestionOptionType;
  next: number | null;
  prev: number | null;
  database_field: string | null;
  is_active: boolean;
  is_delete: boolean;
  created_at?: string;
  updated_at?: string;
  image_url?: string;
  /** Items from option list (when list_id is set) - used for single_select/multiselect */
  list_items?: ChatFlowQuestionOptionListItemRef[];
}

export interface ChatFlowQuestionOptionList {
  list_id: number;
  name: string;
  is_delete: boolean;
  created_at?: string;
  updated_at?: string;
  chat_flow_question_option_list_items?: ChatFlowQuestionOptionListItem[];
}

export interface ChatFlowQuestionOptionListItem {
  item_id: number;
  list_id: number;
  name: string;
  is_delete: boolean;
  created_at?: string;
  updated_at?: string;
}

// DTOs for creating/updating
export interface CreateChatFlowDto {
  flow_name: string;
}

export interface UpdateChatFlowDto {
  flow_name?: string;
  start_question_id?: number | null;
  is_live?: boolean;
}

export interface CreateChatFlowQuestionDto {
  flow_id: number;
  question_text: string;
  question_type: QuestionType;
  is_back_button?: boolean;
  is_skip_button?: boolean;
  next?: number | null;
  prev?: number | null;
  placeholder_text?: string | null;
  database_field?: QuestionDatabaseField | null;
  tags?: string;
  is_generate_order?: boolean;
  options?: QuestionOptionJson[]; // New JSON format
  chat_flow_question_options?: CreateChatFlowQuestionOptionDto[]; // Legacy format
}

export interface UpdateChatFlowQuestionDto {
  question_text?: string;
  question_type?: QuestionType;
  is_back_button?: boolean;
  is_skip_button?: boolean;
  next?: number | null;
  prev?: number | null;
  placeholder_text?: string | null;
  database_field?: QuestionDatabaseField | null;
  tags?: string;
  is_generate_order?: boolean;
  is_active?: boolean;
  options?: QuestionOptionJson[]; // JSON format options
}

export interface CreateChatFlowQuestionOptionDto {
  question_id?: number;
  option_text: string;
  option_placeholder_text?: string;
  option_type?: QuestionOptionType;
  next?: number | null;
  list_id?: number | null;
  image_url?: string;
  database_field?: string | null;
}

export interface UpdateChatFlowQuestionOptionDto {
  option_text?: string;
  option_placeholder_text?: string;
  option_type?: QuestionOptionType;
  next?: number | null;
  image_url?: string;
  database_field?: string | null;
  is_active?: boolean;
}

export const chatflowApi = createApi({
  reducerPath: "chatflowApi",
  baseQuery: axiosBaseQuery(),
  tagTypes: ["ChatFlow", "ChatFlowQuestion", "ChatFlowQuestionOption"],
  endpoints: (builder) => ({
    // ChatFlow endpoints
    getChatFlows: builder.query<Response<ChatFlow[]>, void>({
      query: () => ({
        url: "chat-flow",
        method: "GET",
      }),
      // Note: Backend findAll() currently returns a string placeholder
      // You'll need to implement it to return actual flows
      transformResponse: (response: any) => {
        // If backend returns string placeholder, return empty array
        if (typeof response === 'string') {
          return { success: true, code: 200, message: '', data: [] };
        }
        return response;
      },
      providesTags: ["ChatFlow"],
    }),

    getChatFlow: builder.query<Response<ChatFlow>, number>({
      query: (id) => ({
        url: `chat-flow/${id}`,
        method: "GET",
      }),
      providesTags: (_result, _error, id) => [{ type: "ChatFlow", id }],
    }),

    createChatFlow: builder.mutation<Response<ChatFlow>, CreateChatFlowDto>({
      query: (data) => ({
        url: "chat-flow/create",
        method: "POST",
        data,
      }),
      invalidatesTags: ["ChatFlow"],
    }),

    updateChatFlow: builder.mutation<
      Response<ChatFlow>,
      { id: number; data: UpdateChatFlowDto }
    >({
      query: ({ id, data }) => ({
        url: `chat-flow/${id}`,
        method: "PATCH",
        data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "ChatFlow", id },
        "ChatFlow",
      ],
    }),

    deleteChatFlow: builder.mutation<Response<void>, number>({
      query: (id) => ({
        url: `chat-flow/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["ChatFlow"],
    }),

    cloneChatFlow: builder.mutation<
      Response<ChatFlow>,
      { id: number; flow_name?: string }
    >({
      query: ({ id, flow_name }) => ({
        url: `chat-flow/${id}/clone`,
        method: "POST",
        data: flow_name != null ? { flow_name } : undefined,
      }),
      invalidatesTags: ["ChatFlow"],
    }),

    getOptionLists: builder.query<Response<ChatFlowQuestionOptionList[]>, void>({
      query: () => ({
        url: "chat-flow/option-lists",
        method: "GET",
      }),
      transformResponse: (response: any) => {
        const data = Array.isArray(response) ? response : response?.data ?? [];
        return { success: true, code: 200, message: '', data };
      },
      providesTags: ["ChatFlow"],
    }),

    // ChatFlowQuestion endpoints
    // Note: Questions are included in the chat-flow findOne response
    getChatFlowQuestions: builder.query<
      Response<ChatFlowQuestion[]>,
      number
    >({
      query: (flowId) => ({
        url: `chat-flow/${flowId}`,
        method: "GET",
      }),
      transformResponse: (response: Response<any>) => {
        // Backend returns questions in chat_flow_questions field
        return {
          ...response,
          data: response.data?.chat_flow_questions || [],
        } as Response<ChatFlowQuestion[]>;
      },
      providesTags: ["ChatFlowQuestion"],
    }),

    getChatFlowQuestion: builder.query<
      Response<ChatFlowQuestion>,
      number
    >({
      query: (id) => ({
        url: `chat-flow-question/${id}`,
        method: "GET",
      }),
      providesTags: (_result, _error, id) => [{ type: "ChatFlowQuestion", id }],
    }),

    createChatFlowQuestion: builder.mutation<
      Response<ChatFlowQuestion>,
      CreateChatFlowQuestionDto
    >({
      query: (data) => ({
        url: "chat-flow-question/create",
        method: "POST",
        data,
      }),
      invalidatesTags: ["ChatFlowQuestion", "ChatFlow"],
    }),

    updateChatFlowQuestion: builder.mutation<
      Response<ChatFlowQuestion>,
      { id: number; data: UpdateChatFlowQuestionDto }
    >({
      // Note: PATCH endpoint is commented out in backend controller
      // You'll need to uncomment and implement it in the backend
      query: ({ id, data }) => ({
        url: `chat-flow-question/${id}`,
        method: "PATCH",
        data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "ChatFlowQuestion", id },
        "ChatFlowQuestion",
        "ChatFlow",
      ],
    }),

    deleteChatFlowQuestion: builder.mutation<Response<void>, number>({
      query: (id) => ({
        url: `chat-flow-question/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["ChatFlowQuestion", "ChatFlow"],
    }),

    // ChatFlowQuestionOption endpoints
    // Note: Options are created as part of question creation (nested in CreateChatFlowQuestionDto)
    // If you need separate endpoints for options, you'll need to create a controller in the backend
    // For now, options are managed through the question endpoints
    
    // This endpoint doesn't exist yet - options come with questions
    getChatFlowQuestionOptions: builder.query<
      Response<ChatFlowQuestionOption[]>,
      number
    >({
      query: (questionId) => ({
        url: `chat-flow-question/${questionId}`,
        method: "GET",
      }),
      transformResponse: (response: Response<ChatFlowQuestion>) => {
        return {
          ...response,
          data: response.data?.chat_flow_question_options || [],
        } as Response<ChatFlowQuestionOption[]>;
      },
      providesTags: ["ChatFlowQuestionOption"],
    }),

    // Note: Options are created/updated as part of question creation/update
    // If you need separate endpoints, create a chat-flow-question-option controller in backend
    createChatFlowQuestionOption: builder.mutation<
      Response<ChatFlowQuestionOption>,
      CreateChatFlowQuestionOptionDto
    >({
      // This endpoint doesn't exist - options should be created with questions
      // Or create a controller for this in backend
      query: (data) => ({
        url: "chat-flow-question-option/create",
        method: "POST",
        data,
      }),
      invalidatesTags: ["ChatFlowQuestionOption", "ChatFlowQuestion"],
    }),

    updateChatFlowQuestionOption: builder.mutation<
      Response<ChatFlowQuestionOption>,
      { id: number; data: UpdateChatFlowQuestionOptionDto }
    >({
      // This endpoint doesn't exist - create a controller for this in backend
      query: ({ id, data }) => ({
        url: `chat-flow-question-option/${id}`,
        method: "PATCH",
        data,
      }),
      invalidatesTags: ["ChatFlowQuestionOption", "ChatFlowQuestion"],
    }),

    deleteChatFlowQuestionOption: builder.mutation<Response<void>, number>({
      // This endpoint doesn't exist - create a controller for this in backend
      query: (id) => ({
        url: `chat-flow-question-option/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["ChatFlowQuestionOption", "ChatFlowQuestion"],
    }),
  }),
});

export const {
  useGetChatFlowsQuery,
  useGetChatFlowQuery,
  useGetOptionListsQuery,
  useCreateChatFlowMutation,
  useUpdateChatFlowMutation,
  useDeleteChatFlowMutation,
  useCloneChatFlowMutation,
  useGetChatFlowQuestionsQuery,
  useGetChatFlowQuestionQuery,
  useCreateChatFlowQuestionMutation,
  useUpdateChatFlowQuestionMutation,
  useDeleteChatFlowQuestionMutation,
  useGetChatFlowQuestionOptionsQuery,
  useCreateChatFlowQuestionOptionMutation,
  useUpdateChatFlowQuestionOptionMutation,
  useDeleteChatFlowQuestionOptionMutation,
} = chatflowApi;
